// Accumulation Calculator Comprehensive Tutorial Guide
// Detailed tutorial covering all contribution types, caps, TTR, work test, and strategies
// Last Updated: October 2026
// Caps / LISTO / co-contribution / spouse offset driven from FinancialConfig (FY 2026-27)

import React, { useState, useEffect } from 'react';
import { FinancialConfig } from './FinancialConfig.js';

const fmtAud = (n) => `$${Number(n).toLocaleString('en-AU')}`;

export const AccumulationTutorialGuide = () => {
    const [activeStep, setActiveStep] = useState(0);
    const tutorialRef = React.useRef(null);
    const concessionalCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
    const co = FinancialConfig.CONTRIBUTION_CAPS.CO_CONTRIBUTION;
    const listo = FinancialConfig.CONTRIBUTION_CAPS.LISTO;
    const spouse = FinancialConfig.TAX_OFFSETS.SPOUSE_CONTRIBUTION;
    const d296 = FinancialConfig.DIVISION_296;

    // Auto-scroll to top of tutorial when step changes
    useEffect(() => {
        if (tutorialRef.current) {
            tutorialRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeStep]);

    const tutorialSteps = [
        {
            title: "Welcome to the Accumulation Calculator",
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700">
                        The Accumulation Calculator projects your superannuation growth during your <strong>working years</strong> 
                        (from now until retirement). It handles complex Australian super rules including contribution caps, 
                        carry-forward provisions, TTR strategies, work tests, and spouse/government contributions.
                    </p>
                    <div className="bg-indigo-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-indigo-800 mb-2">Key Features:</h4>
                        <ul className="list-disc list-inside text-indigo-700 space-y-1">
                            <li><strong>Contribution Management:</strong> Tracks concessional and non-concessional contributions against caps</li>
                            <li><strong>Carry-Forward Provisions:</strong> Use unused concessional caps from past 5 years</li>
                            <li><strong>Bring-Forward Rule:</strong> Make 3 years of non-concessional contributions at once</li>
                            <li><strong>Government Co-Contribution:</strong> Free money from government (up to $500/year)</li>
                            <li><strong>Spouse Contributions:</strong> Tax offset for contributing to low-income spouse's super</li>
                            <li><strong>Downsizer Contribution:</strong> $300k per person from home sale (age 55+)</li>
                            <li><strong>TTR Strategy:</strong> Transition to Retirement pension while still working</li>
                            <li><strong>Work Test:</strong> Auto-applies for ages 67-74</li>
                            <li><strong>Historical vs. Monte Carlo:</strong> Test with real market data or random scenarios</li>
                        </ul>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">Who Should Use This:</h4>
                        <ul className="list-disc list-inside text-yellow-700 space-y-1">
                            <li>Anyone still working and accumulating super</li>
                            <li>Those planning pre-retirement contributions</li>
                            <li>People wanting to maximize super before retirement</li>
                            <li>TTR strategy implementers (age 60+)</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Basic Inputs & Superannuation Guarantee",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Foundation Inputs:</h4>
                    
                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📅 Current Age & Retirement Age</h5>
                        <p className="text-sm text-gray-700 mb-2">Determines accumulation timeline:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li><strong>Current Age:</strong> Your age today</li>
                            <li><strong>Retirement Age:</strong> When you'll stop working</li>
                            <li><strong>Preservation Age:</strong> Auto-calculated (55-60 based on birth year)</li>
                            <li>Years to accumulate = Retirement Age - Current Age</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Tip:</strong> You can access super from preservation age, but often better to wait until pension age (67) for Age Pension
                        </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💰 Current Super Balance</h5>
                        <p className="text-sm text-gray-700 mb-2">Your super balance today:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Check myGov or super fund app for exact figure</li>
                            <li>This is your starting point - it will grow with contributions and returns</li>
                            <li>For couples: Enter each partner's balance separately</li>
                        </ul>
                        </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">💼 Current Wage (Annual Salary)</h5>
                        <p className="text-sm text-green-700 mb-2">Your gross annual salary - automatically calculates Superannuation Guarantee (SG):</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Current SG rate:</strong> {(FinancialConfig.SUPERANNUATION_GUARANTEE.RATE * 100).toFixed(0)}% (from 1 July 2025; unchanged for 2026-27)</li>
                            <li><strong>Example:</strong> $100,000 wage → $12,000 SG contribution</li>
                            <li>SG is automatically added to your super (counted toward the {fmtAud(concessionalCap)} concessional cap)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Calculator shows:</strong> &quot;SG: $XX,XXX | Total: $XX,XXX | Cap: {fmtAud(concessionalCap)}&quot;
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📈 Annual Wage Growth (above inflation) (%)</h5>
                        <p className="text-sm text-blue-700 mb-2">Real wage growth (beyond inflation):</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Default: 0.3%</strong> - Based on actual 20-year average wage growth in Australia</li>
                            <li>Conservative: 0% (wage keeps pace with inflation only)</li>
                            <li>Optimistic: 0.5-1% (promotions, career progression)</li>
                            <li>Your wage is assumed to grow by inflation PLUS this percentage</li>
                            <li>Affects SG contributions which grow with your wage</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Note:</strong> Calculator uses 0.3% instead of ASIC's 1.2% because actual Australian wage growth over the last 20 years has averaged only 0.3% above inflation.
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Concessional Contributions (Pre-Tax)",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Understanding Concessional (Pre-Tax) Contributions:</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💙 What Are Concessional Contributions?</h5>
                        <p className="text-sm text-blue-700 mb-2">Pre-tax contributions taxed at 15% inside super (vs your marginal rate):</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Includes:</strong> SG from employer + salary sacrifice + personal deductible contributions</li>
                            <li><strong>Cap:</strong> ${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()}/year (current rates)</li>
                            <li><strong>Tax benefit:</strong> If your marginal rate is 32.5%, you save 17.5% by contributing to super</li>
                        </ul>
                    </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">➕ Additional Concessional Contributions ($/year)</h5>
                        <p className="text-sm text-green-700 mb-2">Extra contributions BEYOND employer SG:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Salary sacrifice:</strong> Pre-tax from employer</li>
                            <li><strong>Personal deductible:</strong> After-tax, claim tax deduction</li>
                            <li>Calculator shows: SG + Additional vs ${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()} cap</li>
                            <li><strong>Warning:</strong> Exceeding cap = excess taxed at ~47%</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Example:</strong> Wage $100k → SG $12,000 → Can add ${(FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL - 12000).toLocaleString()} more to reach the cap
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">📚 Unused Concessional Cap (Carry-Forward)</h5>
                        <p className="text-sm text-purple-700 mb-2">Use unused caps from past 5 years (2019 onwards):</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Eligibility:</strong> Total Super Balance &lt; $500k on June 30 previous year</li>
                            <li><strong>Example:</strong> Only contributed $20k last year → unused space carries forward</li>
                            <li>Can accumulate up to 5 years of unused caps</li>
                            <li>Use oldest caps first (FIFO - first in, first out)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Scenario:</strong> TSB = $400k, unused caps = $25k → Can contribute ${(FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL + 25000).toLocaleString()} this year (current cap + $25k)
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ Division 293 Tax</h5>
                        <p className="text-sm text-red-700 mb-2">High income earners pay extra tax on concessional contributions:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Income &gt; $250,000:</strong> Pay additional 15% tax (total 30% instead of 15%)</li>
                            <li>Applies to income + concessional contributions</li>
                            <li>Still better than marginal rate (45% + 2% Medicare Levy)</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Non-Concessional Contributions & Bring-Forward",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">After-Tax (Non-Concessional) Contributions:</h4>
                    
                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">💛 What Are Non-Concessional Contributions?</h5>
                        <p className="text-sm text-orange-700 mb-2">After-tax money you add to super:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Already taxed:</strong> No tax paid when contributed</li>
                            <li><strong>Cap:</strong> ${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}/year (current rates)</li>
                            <li><strong>When to use:</strong> Have extra savings, windfall, bonus</li>
                            <li><strong>Benefit:</strong> Grows tax-free in pension phase (over age 60)</li>
                            </ul>
                        </div>

                        <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🚀 Bring-Forward Rule (Triple Cap)</h5>
                        <p className="text-sm text-blue-700 mb-2">Accelerate 3 years of contributions into one:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Normal cap:</strong> ${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}/year</li>
                            <li><strong>Bring-forward:</strong> ${FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION.toLocaleString()} in year 1 (then $0 for next 2 years)</li>
                            <li><strong>Eligibility:</strong> Age &lt; 75 AND Total Super Balance &lt; ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Use case:</strong> Inheritance, property sale, large bonus → Get money into super quickly
                        </div>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Calculator handling:</strong> Check the "Use Bring-Forward Rule" box → cap changes to ${FinancialConfig.BRING_FORWARD_RULE.MAXIMUM_CONTRIBUTION.toLocaleString()}
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ Total Super Balance (TSB) Restriction</h5>
                        <p className="text-sm text-yellow-700 mb-2">If TSB ≥ ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M on June 30 → Cannot make non-concessional contributions next year</p>
                        <div className="bg-yellow-100 p-2 rounded text-xs mt-2">
                            <div className="font-semibold text-yellow-900">Example (current rates):</div>
                            <div className="text-yellow-800">• TSB just under the general TBC → Can still contribute the annual non-concessional cap</div>
                            <div className="text-yellow-800">• TSB at or above the general TBC → Cannot make non-concessional contributions</div>
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">❌ Exceeding Caps = Massive Tax!</h5>
                        <p className="text-sm text-red-700 mb-2">Penalties for going over limits:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Excess concessional:</strong> Taxed at marginal rate + interest charge</li>
                            <li><strong>Excess non-concessional:</strong> Taxed at 47% or must be withdrawn</li>
                            <li>Calculator warns you: Red "⚠️ Exceeds cap" message</li>
                            </ul>
                        </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📝 Multiple Contributions Feature</h5>
                        <p className="text-sm text-green-700 mb-2">Click "+" to add separate contribution entries:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li>Make varying contributions in different years</li>
                            <li>Model sporadic bonuses or inheritance</li>
                            <li>Each entry has year and amount</li>
                            <li>Calculator totals them and checks against caps</li>
                            </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Government Co-Contribution & Spouse Contributions",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Free Money from the Government:</h4>
                    
                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🎁 Government Co-Contribution (Up to $500/year)</h5>
                        <p className="text-sm text-green-700 mb-2">Government matches your after-tax contributions:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Maximum:</strong> {fmtAud(co.MAX_AMOUNT)} if you contribute {fmtAud(co.MAX_ELIGIBLE_CONTRIBUTION)} and earn under {fmtAud(co.MIN_INCOME_THRESHOLD)} (2026-27)</li>
                            <li><strong>Scales down:</strong> Reduces as income increases to {fmtAud(co.MAX_INCOME_THRESHOLD)}</li>
                            <li><strong>Eligibility:</strong> 10% or more of income from work/business; under age {co.AGE_LIMIT} on 30 June</li>
                            <li><strong>Type:</strong> Must be non-concessional (after-tax) contribution</li>
                            </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Example:</strong> Income near the lower threshold, contribute {fmtAud(co.MAX_ELIGIBLE_CONTRIBUTION)} after-tax → Government can add up to {fmtAud(co.MAX_AMOUNT)} for free
                        </div>
                        <div className="mt-2 p-2 bg-green-100 text-xs text-green-800 rounded">
                            <strong>Calculator:</strong> Check "Enable Co-Contribution" box → Auto-calculates based on your income
                        </div>
                    </div>

                    <div className="bg-teal-50 p-4 rounded-lg">
                        <h5 className="font-medium text-teal-800 mb-2">LISTO — contributions tax refund (up to $500/year)</h5>
                        <p className="text-sm text-teal-700 mb-2">Separate from the co-contribution: refunds up to 15% of concessional contributions (SG and voluntary concessional) when adjusted taxable income is low.</p>
                        <ul className="list-disc list-inside text-teal-600 text-sm space-y-1 ml-4">
                            <li><strong>2026-27:</strong> Full offset only when adjusted taxable income is at or below {fmtAud(listo.MAX_INCOME_THRESHOLD)} — <em>no taper</em> (cliff eligibility)</li>
                            <li><strong>Maximum:</strong> {fmtAud(listo.MAX_OFFSET)} (15% of concessional contributions, capped)</li>
                            <li><strong>Projection:</strong> Uses wage as ATI unless you enter the optional ATI field</li>
                        </ul>
                        <div className="mt-2 p-2 bg-teal-100 text-xs text-teal-800 rounded">
                            <strong>Calculator:</strong> Check &quot;Include LISTO&quot; on the contributions step
                        </div>
                    </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">💑 Spouse Contribution & Tax Offset</h5>
                        <p className="text-sm text-purple-700 mb-2">Contribute to low-income spouse's super and get tax offset:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Maximum offset:</strong> {fmtAud(spouse.MAX_OFFSET)}/year if you contribute $3,000</li>
                            <li><strong>Spouse income limit:</strong> under {fmtAud(spouse.THRESHOLD_1)} (full offset), scales down to {fmtAud(spouse.THRESHOLD_2)}</li>
                            <li><strong>Contribution must be non-concessional</strong> (after-tax)</li>
                            <li><strong>Benefit:</strong> {fmtAud(spouse.MAX_OFFSET)} back on your tax return + spouse&apos;s super grows</li>
                            </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> Your income $120k, spouse income $30k → Contribute $3k to spouse → Get {fmtAud(spouse.MAX_OFFSET)} tax refund
                        </div>
                        <div className="mt-2 p-2 bg-purple-100 text-xs text-purple-800 rounded">
                            <strong>Calculator:</strong> Enter amount in &quot;Spouse Contribution&quot; field → Auto-calculates offset
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🎯 Combining Strategies</h5>
                        <p className="text-sm text-blue-700 mb-2">You can use multiple strategies together:</p>
                        <div className="bg-blue-100 p-3 rounded text-xs mt-2">
                            <div className="font-semibold text-blue-900 mb-2">Example Household Strategy (Combined income $150k):</div>
                            <div className="text-blue-800 space-y-1">
                                <div>• <strong>Higher earner:</strong> Salary sacrifice toward the {fmtAud(concessionalCap)} concessional cap</div>
                                <div>• <strong>Lower earner (spouse earning $35k):</strong> Contribute $3k to their super</div>
                                <div>• <strong>Benefit:</strong> {fmtAud(spouse.MAX_OFFSET)} tax offset PLUS spouse&apos;s super grows</div>
                                <div>• <strong>If spouse income is under {fmtAud(co.MAX_INCOME_THRESHOLD)}:</strong> They may also get the government co-contribution</div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Downsizer Contribution",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Downsizer Contribution - Special $300k Allowance:</h4>
                    
                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🏠 What Is Downsizer Contribution?</h5>
                        <p className="text-sm text-orange-700 mb-2">Contribute up to $300,000 per person from selling your home:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Age requirement:</strong> 55+ (changed from 60+ recently)</li>
                            <li><strong>Eligibility:</strong> Owned home for 10+ years, eligible for CGT exemption</li>
                            <li><strong>Amount:</strong> Up to $300k per person (couple = $600k total)</li>
                            <li><strong>Does NOT count</strong> toward concessional or non-concessional caps!</li>
                            <li><strong>One-time only:</strong> Can only use once in lifetime</li>
                            </ul>
                        </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💰 How to Use in Calculator</h5>
                        <div className="text-sm text-blue-700 space-y-2">
                            <div><strong>Downsizer Contribution ($):</strong> Enter amount (up to $300,000)</div>
                            <div><strong>Downsizer Year:</strong> Which year from now you'll sell (e.g., "3" = 3 years from now)</div>
                        </div>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Calculator impact:</strong> Contribution added in that specific year, super balance jumps up
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 Why This Is Powerful</h5>
                        <p className="text-sm text-purple-700 mb-2">Unique benefits:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Bypass normal caps:</strong> Even if TSB &gt; ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M, can still contribute</li>
                            <li><strong>No age limit:</strong> Works even at 75+ (unlike normal non-concessional)</li>
                            <li><strong>Tax advantages:</strong> Money grows tax-free in pension phase</li>
                            <li><strong>Estate planning:</strong> Super has better death benefit tax treatment than cash</li>
                            </ul>
                        </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">📝 Real-World Scenario</h5>
                        <div className="bg-yellow-100 p-3 rounded text-xs">
                            <div className="font-semibold text-yellow-900 mb-2">Couple aged 62, both still working:</div>
                            <div className="text-yellow-800 space-y-1">
                                <div>• Current super: $800k combined</div>
                                <div>• Plan to downsize at 65 (3 years)</div>
                                <div>• Home worth $1.2M, will buy unit for $600k</div>
                                <div>• <strong>Can contribute:</strong> $600k to super ($300k each)</div>
                                <div>• <strong>New super at 65:</strong> ~$1.6M (including growth)</div>
                                <div>• <strong>Result:</strong> Dramatically improved retirement security</div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Transition to Retirement (TTR) Strategy",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">TTR - Keep Working While Accessing Super:</h4>
                    
                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">💼 What Is TTR?</h5>
                        <p className="text-sm text-green-700 mb-2">Transition to Retirement strategy - access super WHILE still working:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Age requirement:</strong> Preservation age (55-60) to retirement age</li>
                            <li><strong>Drawdown limits:</strong> 4% minimum, 10% maximum per year</li>
                            <li><strong>Can't access lump sums</strong> - income stream only</li>
                            <li><strong>Tax on earnings:</strong> 15% (same as accumulation) until age 60, then 0%</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💡 Why Use TTR? (Classic Strategy)</h5>
                        <p className="text-sm text-blue-700 mb-2">Two main reasons:</p>
                        <div className="space-y-2 mt-2">
                            <div className="bg-blue-100 p-3 rounded text-xs">
                                <div className="font-semibold text-blue-900 mb-1">1. Salary Sacrifice Boost (Pre Age-60)</div>
                                <div className="text-blue-800">
                                    • Salary sacrifice more to super (taxed at 15%)<br/>
                                    • Draw same amount back via TTR pension (tax-free if 60+)<br/>
                                    • Net effect: Same take-home pay BUT more super (saved the tax difference)<br/>
                                    • <strong>Note:</strong> Requires age 60+ for tax-free TTR income
                                </div>
                            </div>
                            <div className="bg-blue-100 p-3 rounded text-xs">
                                <div className="font-semibold text-blue-900 mb-1">2. Supplement Reduced Work</div>
                                <div className="text-blue-800">
                                    • Work part-time or reduce hours<br/>
                                    • Top up income with TTR drawdown<br/>
                                    • Gradual transition to full retirement
                                </div>
                            </div>
                        </div>
                    </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎮 Calculator TTR Settings</h5>
                        <div className="text-sm text-purple-700 space-y-2">
                            <div><strong>Enable TTR:</strong> Check this box to activate</div>
                            <div><strong>TTR Start Age:</strong> When to begin (must be ≥ preservation age)</div>
                            <div><strong>TTR Drawdown Rate:</strong> Percentage to withdraw (4-10%)</div>
                            <div><strong>Accumulation Phase %:</strong> What % of super stays in accumulation vs pension</div>
                        </div>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> Balance $400k, Start TTR at 60, Rate 5% → Withdraw $20k/year from super while working
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ TTR Complexity</h5>
                        <p className="text-sm text-yellow-700 mb-2">TTR rules changed in 2017:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li><strong>Before 2017:</strong> 0% tax on earnings (highly beneficial)</li>
                            <li><strong>After 2017:</strong> 15% tax on earnings (same as accumulation)</li>
                            <li><strong>Still beneficial if:</strong> Age 60+ (tax-free income) + salary sacrificing</li>
                            <li><strong>Not as beneficial:</strong> Under age 60 or not salary sacrificing</li>
                            </ul>
                        </div>
                </div>
            )
        },
        {
            title: "Work Test & Age Restrictions",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Age-Based Contribution Rules:</h4>
                    
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ Work Test (Ages 67-74)</h5>
                        <p className="text-sm text-red-700 mb-2">Must meet work test to make voluntary contributions:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Requirement:</strong> Work at least 40 hours in 30 consecutive days</li>
                            <li><strong>Applies to:</strong> Ages 67-74 inclusive</li>
                            <li><strong>Affects:</strong> Personal concessional, non-concessional, spouse contributions</li>
                            <li><strong>Doesn't affect:</strong> Employer SG (mandatory), downsizer contribution</li>
                            </ul>
                        <div className="mt-2 p-2 bg-red-200 text-xs text-red-900 rounded">
                            <strong>Calculator:</strong> "Enable Work Test" checkbox → If unchecked, contributions stop at age 67
                        </div>
                    </div>

                        <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🎂 Age 75+ - Contributions Stop</h5>
                        <p className="text-sm text-orange-700 mb-2">Strict cutoff at age 75:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>No voluntary contributions</strong> allowed (concessional or non-concessional)</li>
                            <li><strong>Exception:</strong> Downsizer contribution (no age limit)</li>
                            <li><strong>Employer SG:</strong> Can continue if still working</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📊 Work Test Exemption</h5>
                        <p className="text-sm text-blue-700 mb-2">One-year exemption if you qualified previous year:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li>If you met work test in previous year, can contribute in current year even if not working</li>
                            <li>TSB must be &lt; $300k at end of previous year</li>
                            <li>One year only - then must meet work test again</li>
                            </ul>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">✅ Calculator Assumptions</h5>
                        <div className="text-sm text-green-700">
                            <div className="font-semibold mb-1">When "Enable Work Test" is checked:</div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4 space-y-1">
                                <li>Calculator assumes you meet work test if you're making contributions at ages 67-74</li>
                                <li>Contributions automatically stop at age 75</li>
                            </ul>
                            <div className="font-semibold mt-2 mb-1">When unchecked:</div>
                            <ul className="list-disc list-inside text-green-600 text-xs ml-4">
                                <li>All voluntary contributions stop at age 67</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Fees, Returns & Advanced Options",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Investment Settings & Fees:</h4>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📈 Assumed Return (%)</h5>
                        <p className="text-sm text-gray-700 mb-2">Expected annual return:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Conservative (high cash/bonds): 4-5%</li>
                            <li>Balanced (mix): 6-7%</li>
                            <li>Growth (high shares): 7-9%</li>
                            <li>Historical average (balanced): ~7.5%</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📉 Inflation Rate (%)</h5>
                        <p className="text-sm text-gray-700 mb-2">Adjust all future values for inflation:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>RBA target: 2-3%</li>
                            <li>Calculator applies to: Wage growth, contribution caps, Age Pension rates</li>
                        </ul>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">💳 Fees Impact</h5>
                        <p className="text-sm text-red-700 mb-2">Three fee types modeled:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Admin Fees ($):</strong> Flat annual fee ($50-$300 typical)</li>
                            <li><strong>Investment Fees (%):</strong> Percentage of balance (0.3% - 1.5% typical)</li>
                            <li><strong>Insurance ($ total/year):</strong> Life/TPD premiums deducted from super</li>
                            </ul>
                        <div className="mt-2 p-2 bg-red-200 text-xs text-red-900 rounded">
                            <strong>Compound effect:</strong> Over 25 years, 1% fees can cost $200k+ on a $500k balance!
                        </div>
                    </div>

                        <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🔄 Portfolio Turnover (%)</h5>
                        <p className="text-sm text-blue-700 mb-2">How often fund managers trade (affects CGT):</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li>Low turnover (index funds): 5-20%</li>
                            <li>Medium turnover: 30-50%</li>
                            <li>High turnover (active trading): 70-100%+</li>
                            <li>Higher turnover = more tax (in accumulation phase only)</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">💚 Dividend Portion (% franked)</h5>
                        <p className="text-sm text-green-700 mb-2">Australian company imputation credits only:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Only applies to the Australian shares allocation</strong> — not S&amp;P 500, international shares, bonds or cash</li>
                            <li>Franking credits reduce tax in accumulation phase</li>
                            <li>Typical ASX portfolios: 70-80% franked</li>
                            <li>Adds roughly 0.5–1.5% to effective returns on the Aussie equity sleeve</li>
                            </ul>
                        </div>

                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <h5 className="font-medium text-slate-800 mb-2">🏦 Division 296 (balances above {fmtAud(d296.THRESHOLD)})</h5>
                        <p className="text-sm text-slate-700 mb-2">
                            From the {d296.FIRST_YEAR_FINANCIAL_YEAR} income year, additional tax can apply to earnings attributable
                            to total super balances above {fmtAud(d296.THRESHOLD)} (and a further tier above {fmtAud(d296.VERY_LARGE_THRESHOLD)}).
                            Projections with large balances can show this impact; treat it as educational modelling, not an ATO assessment.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "SMSF Data Transfer & Multi-Asset Portfolios",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Importing Complex Portfolios from SMSF Calculator:</h4>
                    
                    <div className="bg-indigo-50 p-4 rounded-lg">
                        <h5 className="font-medium text-indigo-800 mb-2">🔄 Transfer from SMSF Calculator</h5>
                        <p className="text-sm text-indigo-700 mb-2">If you've set up your portfolio in the SMSF Calculator's Asset Valuation Manager, you can transfer it directly to this calculator:</p>
                        <ul className="list-disc list-inside text-indigo-600 text-sm space-y-1 ml-4">
                            <li><strong>Complete Portfolio Transfer:</strong> All assets transfer with individual details preserved</li>
                            <li><strong>Multiple Properties:</strong> Each residential/commercial property maintains its own value, rental income, and growth rate</li>
                            <li><strong>Alternative Assets:</strong> Infrastructure funds and other alternatives transfer with individual return rates</li>
                            <li><strong>Collectibles:</strong> Art, antiques, and other collectibles transfer with individual valuations</li>
                            <li><strong>Automatic Allocation:</strong> Portfolio percentages are automatically calculated and locked</li>
                        </ul>
                        <div className="mt-2 p-2 bg-indigo-200 text-xs text-indigo-900 rounded">
                            <strong>How to Transfer:</strong> In SMSF Calculator → Asset Valuation Manager → Scroll to bottom → Click "Transfer to Accumulation Calculator"
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🏘️ Multiple Residential Properties</h5>
                        <p className="text-sm text-purple-700 mb-2">When transferred from SMSF, each property is displayed separately:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Individual Cards:</strong> Each property shown in its own card with name (e.g., "123 Main St Investment Property")</li>
                            <li><strong>Separate Metrics:</strong> Value, rental income, rental yield, and capital growth for each property</li>
                            <li><strong>Read-Only:</strong> Values are fixed from SMSF transfer (this is a projection tool)</li>
                            <li><strong>Tax Treatment:</strong> Rental income taxed at 15% (accumulation phase)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> Property 1: $800K @ 4.5% yield | Property 2: $650K @ 5.2% yield → Both tracked separately in projection
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🏢 Multiple Commercial Properties</h5>
                        <p className="text-sm text-green-700 mb-2">Commercial properties work the same way as residential:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li>Each property maintains individual valuation and rental income</li>
                            <li>Typically higher rental yields than residential (5-7%)</li>
                            <li>Same tax treatment as residential rental income (15% in accumulation)</li>
                            <li>Can have LRBA (Limited Recourse Borrowing Arrangement) details</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">⚡ Infrastructure & Alternative Assets</h5>
                        <p className="text-sm text-blue-700 mb-2">Infrastructure funds and other productive alternatives:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Productive Assets:</strong> Count toward portfolio allocation (e.g., 73% infrastructure)</li>
                            <li><strong>Individual Returns:</strong> Each asset has its own expected return rate</li>
                            <li><strong>Examples:</strong> Infrastructure funds, unlisted property funds, private equity</li>
                            <li><strong>Display:</strong> Shown in dedicated "Infrastructure (Productive Asset)" section</li>
                            <li><strong>Allocation:</strong> Percentage shown in "Productive Asset Breakdown" panel</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Note:</strong> Infrastructure is included in the 100% portfolio allocation requirement
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">🎨 Collectibles (Non-Productive Assets)</h5>
                        <p className="text-sm text-yellow-700 mb-2">Art, antiques, and other collectibles:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li><strong>Non-Productive:</strong> Don't count toward portfolio allocation</li>
                            <li><strong>Capital Growth Only:</strong> No income, only appreciation</li>
                            <li><strong>Tracked Separately:</strong> Shown in results as "Non-Productive Assets"</li>
                            <li><strong>Total Wealth:</strong> Combined with productive assets for overall wealth calculation</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🔒 Locked Portfolio Allocation</h5>
                        <p className="text-sm text-orange-700 mb-2">When data is transferred from SMSF:</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Percentages Fixed:</strong> Portfolio allocation percentages are calculated from SMSF data and locked</li>
                            <li><strong>Must Total 100%:</strong> Productive assets (shares, bonds, cash, infrastructure) must sum to 100%</li>
                            <li><strong>Validation:</strong> Calculator checks allocation before running simulation</li>
                            <li><strong>Breakdown Display:</strong> See exact percentages in "Productive Asset Breakdown" panel</li>
                        </ul>
                        <div className="mt-2 p-2 bg-orange-200 text-xs text-orange-900 rounded">
                            <strong>Example:</strong> Direct Shares 12.9% + Infrastructure 73.0% + Cash 14.0% = 100.0% ✅
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">📊 Results with Non-Productive Assets</h5>
                        <p className="text-sm text-red-700 mb-2">The calculator shows comprehensive wealth tracking:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Productive Assets:</strong> Super balance from shares, bonds, cash, infrastructure (generates income)</li>
                            <li><strong>Non-Productive Assets:</strong> Separate line showing collectibles and property values</li>
                            <li><strong>Combined Wealth:</strong> Total of both productive and non-productive assets</li>
                            <li><strong>Explanations:</strong> Clear descriptions of why assets are categorized differently</li>
                        </ul>
                        <div className="mt-2 p-2 bg-red-200 text-xs text-red-900 rounded">
                            <strong>Why separate?</strong> Productive assets generate retirement income; non-productive assets are wealth but don't produce cash flow
                        </div>
                    </div>

                    <div className="bg-teal-50 p-4 rounded-lg">
                        <h5 className="font-medium text-teal-800 mb-2">✅ Benefits of SMSF Transfer</h5>
                        <ul className="list-disc list-inside text-teal-600 text-sm space-y-1 ml-4">
                            <li><strong>Accuracy:</strong> Each asset's unique characteristics preserved</li>
                            <li><strong>Comprehensive:</strong> See total wealth including non-productive assets</li>
                            <li><strong>Realistic:</strong> Different rental yields and growth rates per property</li>
                            <li><strong>Time-Saving:</strong> No need to re-enter complex portfolio data</li>
                            <li><strong>Consistency:</strong> Same data across SMSF and projection calculators</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Historical vs Monte Carlo Modes",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Two Powerful Analysis Modes:</h4>
                    
                        <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎲 Monte Carlo Mode</h5>
                        <p className="text-sm text-purple-700 mb-2">Runs 1,000 random future scenarios:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>Based on your assumed return and historical volatility</li>
                            <li>Shows range of outcomes (10th, 50th, 90th percentile)</li>
                            <li>Better for: Understanding probability and risk</li>
                            </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Reading results:</strong> "50% chance of $850k or more, 10% chance of $650k or less"
                        </div>
                    </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📊 Historical Mode</h5>
                        <p className="text-sm text-green-700 mb-2">Tests against actual market history since 1960:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li>Uses real S&P 500, bond, cash, and Australian share returns</li>
                            <li>Includes recessions, crashes, boom periods</li>
                            <li>Shows best/worst/median historical outcomes</li>
                            <li>Better for: Stress testing against real crises</li>
                            </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Insight:</strong> "Starting accumulation during dot-com bubble would have still reached $780k"
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🎯 Which Mode to Use?</h5>
                        <ul className="list-disc list-inside text-blue-700 text-sm space-y-1 ml-4">
                            <li><strong>Starting out (20+ years to retirement):</strong> Monte Carlo - broader range of futures</li>
                            <li><strong>Close to retirement (&lt; 10 years):</strong> Historical - see worst actual outcomes</li>
                            <li><strong>Best practice:</strong> Run BOTH, compare median outcomes</li>
                            </ul>
                        </div>
                </div>
            )
        },
        {
            title: "What-If Scenarios & Tips",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Testing Different Situations:</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">➕ Extra Annual Contribution</h5>
                        <p className="text-sm text-blue-700 mb-2">What if you contributed more each year?</p>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Example:</strong> Add $5,000/year → See how much extra super at retirement
                        </div>
                    </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">💰 One-Off Lump Sum</h5>
                        <p className="text-sm text-green-700 mb-2">What if you received a windfall?</p>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Example:</strong> Contribute $50k in year 5 (from inheritance) → Impact on final balance
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">💸 Change Fees</h5>
                        <p className="text-sm text-purple-700 mb-2">Compare your current fund vs lower-cost option:</p>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> Enter "-0.5%" → See savings from switching to cheaper fund
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">✅ Best Practices</h5>
                        <ul className="list-disc list-inside text-orange-700 text-sm space-y-1 ml-4">
                            <li><strong>Maximize employer SG:</strong> Ensure you&apos;re getting the full {(FinancialConfig.SUPERANNUATION_GUARANTEE.RATE * 100).toFixed(0)}% rate</li>
                            <li><strong>Use concessional cap:</strong> If earning $100k+, salary sacrifice toward the {fmtAud(concessionalCap)} cap</li>
                            <li><strong>Government co-contribution:</strong> If eligible (income under {fmtAud(co.MAX_INCOME_THRESHOLD)}), contribute {fmtAud(co.MAX_ELIGIBLE_CONTRIBUTION)} for up to {fmtAud(co.MAX_AMOUNT)}</li>
                            <li><strong>Spouse contribution:</strong> If spouse earns under {fmtAud(spouse.THRESHOLD_1)}, contribute $3k for up to {fmtAud(spouse.MAX_OFFSET)} tax offset</li>
                            <li><strong>Minimize fees:</strong> Switch to low-cost fund if paying &gt; 0.85% total</li>
                            <li><strong>Review insurance:</strong> Do you need it? Costs add up</li>
                            <li><strong>Start early:</strong> Compounding is magic - even small amounts matter</li>
                            </ul>
                        </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">❌ Common Mistakes</h5>
                        <ul className="list-disc list-inside text-red-700 text-sm space-y-1 ml-4">
                            <li><strong>Exceeding caps:</strong> Watch the calculator warnings! Excess = massive tax</li>
                            <li><strong>Forgetting about SG:</strong> Your SG counts toward concessional cap</li>
                            <li><strong>Bring-forward without checking TSB:</strong> Must be under the general transfer balance cap to be eligible</li>
                            <li><strong>Not using carry-forward:</strong> If TSB &lt; $500k, use unused caps!</li>
                            <li><strong>TTR below age 60:</strong> Tax benefit minimal if under 60</li>
                            </ul>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div ref={tutorialRef} className="space-y-6">
            {/* Progress Indicator */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                            Step {activeStep + 1} of {tutorialSteps.length}
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((activeStep + 1) / tutorialSteps.length) * 100}%` }}
                        ></div>
                        </div>
                    </div>
                    </div>
                </div>

            {/* Current Step Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {tutorialSteps[activeStep].title}
                    </h3>
                <div className="prose max-w-none">
                    {tutorialSteps[activeStep].content}
                </div>
                </div>

            {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                        disabled={activeStep === 0}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    ← Previous
                    </button>
                <div className="flex gap-2">
                    {tutorialSteps.map((_, idx) => (
                            <button
                            key={idx}
                            onClick={() => setActiveStep(idx)}
                            className={`w-2 h-2 rounded-full transition ${
                                idx === activeStep ? 'bg-indigo-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                            />
                        ))}
                    </div>
                        <button
                            onClick={() => setActiveStep(Math.min(tutorialSteps.length - 1, activeStep + 1))}
                    disabled={activeStep === tutorialSteps.length - 1}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    Next →
                        </button>
            </div>
        </div>
    );
};

export default AccumulationTutorialGuide;